import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import Modal from '../Modal/Modal';
import { requestsApi, Request } from '../../services/api';
import UTCTime from '../UTCTime/UTCTime';
import SimpleOfferForm from '../SimpleOfferForm/SimpleOfferForm';
import './RequestQuickView.css';

interface RequestQuickViewProps {
  requestId: string | null;
  isOpen: boolean;
  onClose: () => void;
}

function RequestQuickView({ requestId, isOpen, onClose }: RequestQuickViewProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [request, setRequest] = useState<Request | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Simple Text Offer Modal (for Personnel/Material/Other)
  const [showTextOfferModal, setShowTextOfferModal] = useState(false);

  useEffect(() => {
    if (requestId && isOpen) {
      loadRequest();
    }
  }, [requestId, isOpen]);

  const loadRequest = async () => {
    if (!requestId) return;

    try {
      setLoading(true);
      setError('');
      const response = await requestsApi.getById(requestId);
      setRequest(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load request');
      setRequest(null);
    } finally {
      setLoading(false);
    }
  };

  const handleViewFullDetails = () => {
    if (request) {
      navigate(`/requests/${request.id}`);
      onClose();
    }
  };

  const handleMakeOffer = () => {
    if (!request) return;

    // For MEDEVAC requests, navigate directly to fullpage offer creation
    if (request.category === 'MEDEVAC' && request.patients && request.patients.length > 0) {
      navigate(`/requests/${request.id}/offers/create`);
      onClose();
    } else if (['PERSONNEL', 'MATERIAL', 'OTHER'].includes(request.category)) {
      // For Personnel/Material/Other, show simple text offer modal
      setShowTextOfferModal(true);
    } else {
      // For other categories, navigate to detail page
      navigate(`/requests/${request.id}`);
      onClose();
    }
  };


  const getStatusBadgeClass = (status: string) => {
    const statusClasses: { [key: string]: string } = {
      DRAFT: 'badge-draft',
      UNDER_REVIEW: 'badge-review',
      PUBLISHED: 'badge-published',
      UNDER_SELECTION: 'badge-selection',
      ACCEPTED: 'badge-accepted',
      PLANNED: 'badge-planned',
      IN_TRANSIT: 'badge-transit',
      COMPLETED: 'badge-completed',
      CLOSED: 'badge-closed',
      WITHDRAWN: 'badge-withdrawn'
    };
    return statusClasses[status] || 'badge-default';
  };

  const getStatusLabel = (status: string) => {
    const labels: { [key: string]: string } = {
      DRAFT: t('status.draft'),
      UNDER_REVIEW: t('status.underReview'),
      PUBLISHED: t('status.published'),
      UNDER_SELECTION: t('status.underSelection'),
      ACCEPTED: t('status.accepted'),
      PLANNED: t('status.planned'),
      IN_TRANSIT: t('status.inTransit'),
      COMPLETED: t('status.completed'),
      CLOSED: t('status.closed'),
      WITHDRAWN: t('status.withdrawn')
    };
    return labels[status] || status;
  };

  const getCategoryLabel = (category: string) => {
    const labels: { [key: string]: string } = {
      MEDEVAC: t('categories.medevac'),
      PERSONNEL: t('categories.personnel'),
      MATERIAL: t('categories.material'),
      TRANSPORT: t('offers.transport'),
      OTHER: t('categories.other'),
      NEWS: t('categories.news')
    };
    return labels[category] || category;
  };

  const getCategoryBadgeClass = (category: string) => {
    const classes: { [key: string]: string } = {
      MEDEVAC: 'badge-category-medevac',
      PERSONNEL: 'badge-category-personnel',
      MATERIAL: 'badge-category-equipment',
      TRANSPORT: 'badge-category-transport',
      OTHER: 'badge-category-other',
      NEWS: 'badge-category-news'
    };
    return classes[category] || 'badge-category-other';
  };

  if (!isOpen) return null;

  return (
    <Modal
      title={request ? `${request.requestId} - ${t('common.overview')}` : t('common.loading')}
      isOpen={isOpen}
      onClose={onClose}
      width="90%"
      maxWidth="600px"
    >
      <div className="request-quick-view">
        {loading && (
          <div className="quick-view-loading">
            <div className="spinner"></div>
            <p>{t('common.loading')}</p>
          </div>
        )}

        {error && (
          <div className="quick-view-error">
            <p>{error}</p>
          </div>
        )}

        {!loading && !error && request && (
          <>
            {/* Header Section */}
            <div className="quick-view-header">
              <div className="header-title">
                <h2>{request.title}</h2>
                <span className={`badge ${getStatusBadgeClass(request.status)}`}>
                  {getStatusLabel(request.status)}
                </span>
              </div>
              <div className="header-actions">
                {/* Only show Make Offer button if it's not the user's own request, status is PUBLISHED, and not NEWS */}
                {request.status === 'PUBLISHED' && request.creator.id !== user?.id && request.category !== 'NEWS' && (
                  <button onClick={handleMakeOffer} className="btn btn-success btn-sm">
                    {t('offers.makeOffer')}
                  </button>
                )}
                <button onClick={handleViewFullDetails} className="btn btn-primary btn-sm">
                  {t('common.viewDetails')} <ArrowRight size={14} />
                </button>
              </div>
            </div>

            {/* Info Grid */}
            <div className="quick-view-info-grid">
              <div className="info-item">
                <label>{t('requests.fromNation')}</label>
                <span>{request.creator.nation}</span>
              </div>
              <div className="info-item">
                <label>{t('requests.category')}</label>
                <span className={`category-badge ${getCategoryBadgeClass(request.category)}`}>{getCategoryLabel(request.category)}</span>
              </div>
              <div className="info-item">
                <label>{t('requests.urgency')}</label>
                <span className={`badge badge-${request.urgency.toLowerCase()}`}>
                  {request.urgency}
                </span>
              </div>
              <div className="info-item">
                <label>{t('requests.created')}</label>
                <span><UTCTime date={request.createdAt} /></span>
              </div>
              {request.answerBy && (
                <div className="info-item">
                  <label>{t('common.date')}</label>
                  <span className="deadline">
                    <UTCTime date={request.answerBy} />
                  </span>
                </div>
              )}
              {request.publishedAt && (
                <div className="info-item">
                  <label>{t('status.published')}</label>
                  <span><UTCTime date={request.publishedAt} /></span>
                </div>
              )}
            </div>

            {/* Description (short) */}
            {request.description && (
              <div className="quick-view-section">
                <div className="description-text">
                  {request.description.length > 200
                    ? `${request.description.substring(0, 200)}...`
                    : request.description}
                </div>
              </div>
            )}

            {/* Patient count for MEDEVAC */}
            {request.category === 'MEDEVAC' && request.patients && request.patients.length > 0 && (
              <div className="quick-view-section">
                <div className="quick-view-patient-summary">
                  <strong>{request.patients.length}</strong> patient{request.patients.length > 1 ? 's' : ''}
                  {request.exitPoint && <> &middot; {request.exitPoint.name}</>}
                  {request.offers && request.offers.length > 0 && <> &middot; {request.offers.length} offer{request.offers.length > 1 ? 's' : ''}</>}
                </div>
              </div>
            )}
          </>
        )}
      </div>


      {/* Simple Text Offer Modal (for Personnel/Material/Other) */}
      {showTextOfferModal && request && (
        <div className="modal-overlay" onClick={() => setShowTextOfferModal(false)}>
          <div className="modal-content modal-content-large" onClick={(e) => e.stopPropagation()}>
            <SimpleOfferForm
              requestId={request.id}
              onSuccess={() => {
                setShowTextOfferModal(false);
                loadRequest();
                onClose();
              }}
              onCancel={() => {
                setShowTextOfferModal(false);
              }}
            />
          </div>
        </div>
      )}
    </Modal>
  );
}

export default RequestQuickView;
